import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { formatCountdown } from "../lib/time";

export interface PairingCodeHeroProps {
  code: string;
  expiresAt: number;
}

function formatCode(code: string): string {
  // 6 digits → "742-918"
  const clean = code.replace(/\D/g, "");
  if (clean.length === 6) return `${clean.slice(0, 3)}-${clean.slice(3)}`;
  return code;
}

export function PairingCodeHero({ code, expiresAt }: PairingCodeHeroProps) {
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const remaining = Math.max(0, expiresAt - now);
  const expired = remaining === 0;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatCode(code));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-6">
      <div className="micro">Pairing code</div>
      <div className="flex items-center gap-3">
        <div
          className="font-mono text-[44px] font-semibold tracking-[0.12em] text-primary"
          aria-label={`Pairing code ${code}`}
        >
          {formatCode(code)}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="iconbtn"
          aria-label="Copy pairing code"
          title="Copy"
        >
          {copied ? <Check size={16} className="text-status-running" /> : <Copy size={16} />}
        </button>
      </div>
      <div
        className={`font-mono text-[12px] ${expired ? "text-status-error" : "text-tertiary"}`}
        aria-live="polite"
      >
        {expired ? "Expired — generate a new code" : `Expires in ${formatCountdown(remaining)}`}
      </div>
    </div>
  );
}
